import React, { useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { toast } from "sonner";
import { Eraser } from "lucide-react";

export function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    const cx = (t ? t.clientX : e.clientX) - r.left;
    const cy = (t ? t.clientY : e.clientY) - r.top;
    return [cx * (ref.current.width / r.width), cy * (ref.current.height / r.height)];
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext("2d"); const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#111827"; ctx.stroke(); };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(""); };
  return (
    <div className="space-y-2">
      <canvas ref={ref} width={560} height={200}
        className="w-full border-2 border-dashed border-border rounded-sm bg-white cursor-crosshair"
        style={{ touchAction: "none" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-canvas" />
      <Button type="button" variant="outline" size="sm" onClick={clear} className="rounded-sm gap-1 text-xs" data-testid="signature-clear"><Eraser className="h-3.5 w-3.5" />Limpiar</Button>
    </div>
  );
}

export function SignDialog({ open, onOpenChange, docId, docNumber, onSigned }) {
  const [img, setImg] = useState("");
  const [typed, setTyped] = useState("");
  const [mode, setMode] = useState("draw");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (mode === "draw" && !img) return toast.error("Dibuja tu firma en el recuadro");
    if (mode === "type" && !typed.trim()) return toast.error("Escribe tu nombre completo");
    setSaving(true);
    try {
      await api.post(`/payroll/${docId}/sign`, mode === "draw" ? { signature_image: img } : { signature_typed: typed.trim() });
      toast.success("Voucher firmado correctamente");
      setImg(""); setTyped(""); onOpenChange(false); onSigned && onSigned();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle>Firmar voucher {docNumber || ""}</DialogTitle>
          <DialogDescription>Firma de conformidad. Puedes dibujar tu firma con el dedo/mouse o generar una firma electrónica.</DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode} className="space-y-3">
          <TabsList className="rounded-sm">
            <TabsTrigger value="draw" className="rounded-sm" data-testid="sign-tab-draw">Dibujar firma</TabsTrigger>
            <TabsTrigger value="type" className="rounded-sm" data-testid="sign-tab-type">Firma electrónica</TabsTrigger>
          </TabsList>
          <TabsContent value="draw"><SignaturePad onChange={setImg} /></TabsContent>
          <TabsContent value="type" className="space-y-2">
            <Label className="text-xs font-bold uppercase">Nombre completo</Label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Tu nombre completo" className="rounded-sm" data-testid="sign-typed" />
            <p className="text-xs text-muted-foreground">Se registrará como firma electrónica con tu nombre, fecha y hora.</p>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="rounded-sm" onClick={submit} disabled={saving} data-testid="sign-submit">{saving ? "Firmando..." : "Firmar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
